import i18next from '@/i18n';
import { EMaterialType, ILibraryMaterial } from '@/service/library/types';
import { defaultOutlineColor, filledCircleColor, floor2dColor, hellowCircleColor } from '../constant/entity';
import { defaultPlaneColor, glassPlaneColor } from '../entities/plane/const';
import { IMaterialConfig } from '@/editor/data/material/material';
import { EFaceType } from '../entities/type';
import { materialDefaultImageUrl } from '@/assets/library/material';
import { LineDashedMaterial, MeshBasicMaterial } from 'three';
import { LineMaterial } from 'three/examples/jsm/Addons.js';

export interface IStoreMaterial {
  id: string;
  data: IMaterialConfig;
  name: string;
  type: EMaterialType;
  isDefault?: boolean;
  display: ILibraryMaterial['display'];
  properties: ILibraryMaterial['properties'];
}

const DEFAULT_FLOOR_MATERIAL: IStoreMaterial = {
  id: 'floor-0',
  name: i18next.t('draw.house.material.no'),
  type: EMaterialType.Floor,
  isDefault: true,
  display: {
    thumbnail: materialDefaultImageUrl.floor,
    images: [
      {
        key: 'default',
        url: materialDefaultImageUrl.floor
      }
    ]
  },
  properties: {
    price: 0,
    brand: ''
  },
  data: {
    id: 'floor-0',
    color: floor2dColor
  }
};

const DEFAULT_GLASS_MATERIAL: IStoreMaterial = {
  id: 'glass-0',
  name: '',
  display: {
    thumbnail: '',
    images: []
  },
  isDefault: true,
  type: EMaterialType.GlassFace,
  properties: {},
  data: {
    id: 'glass-0',
    color: glassPlaneColor,
    opacity: 0.3,
    transparent: true,
    roughness: 0.1,
    metalness: 0,
    envMapIntensity: 1
  }
};

const DEFAULT_WALLFACE_MATERIAL: IStoreMaterial = {
  id: 'wall-0', // 所有ID先临时写死ID
  name: i18next.t('draw.house.material.no'),
  type: EMaterialType.WallFace,
  isDefault: true,
  display: {
    thumbnail: materialDefaultImageUrl.wall,
    images: [
      {
        key: 'default',
        url: materialDefaultImageUrl.wall
      }
    ]
  },
  properties: {
    price: 0,
    brand: ''
  },
  data: {
    id: 'wall-0',
    color: defaultPlaneColor
  }
};

export class MaterialStore {
  #materials: IStoreMaterial[] = [];

  // 全局共享材质，待优化
  static sharedMaterials = {
    dashLine: new LineDashedMaterial({
      color: defaultOutlineColor,
      scale: 10,
      dashSize: 0.5,
      gapSize: 0.5
    }),
    extremePointFilled: new MeshBasicMaterial({ color: filledCircleColor }),
    extremePointOutline: new LineMaterial({ color: hellowCircleColor, linewidth: 0.5 })
  };

  constructor() {}

  public loadMaterials(materials: ILibraryMaterial[]) {
    this.#materials = [DEFAULT_FLOOR_MATERIAL, DEFAULT_WALLFACE_MATERIAL, DEFAULT_GLASS_MATERIAL];
    this.#materials.push(
      ...materials.map((material) => ({
        ...material,
        data: {
          id: material.id,
          texture: material.texture
        }
      }))
    );
  }

  getMaterialData(id: string) {
    return this.#materials.find((mat) => mat.id === id)?.data || null;
  }

  getFaceMaterial(id: string) {
    return this.#materials.find((mat) => mat.id === id && mat.type === EMaterialType.WallFace);
  }

  getFloorMaterial(id: string) {
    return this.#materials.find((mat) => mat.id === id && mat.type === EMaterialType.Floor);
  }

  getDefaultMaterial(type?: EFaceType) {
    switch (type) {
      case EFaceType.FloorFace:
        return this.#materials.find((mat) => mat.isDefault && mat.type === EMaterialType.Floor);
      case EFaceType.SepWallFace:
        return this.#materials.find((mat) => mat.isDefault && mat.type === EMaterialType.GlassFace);
      default:
        return this.#materials.find((mat) => mat.isDefault && mat.type === EMaterialType.WallFace);
    }
  }

  get materials() {
    return this.#materials;
  }
}
